/**
 * K-T-11 — Layer-3 hook for canonical per-vehicle-year-bucket
 * efficiency.
 *
 * Thin wrapper around `kpi_efficiency_by_vehicle_year_group(shop_id,
 * start_at, end_at)`. Returns one `VehicleYearGroupEfficiencyResult`
 * per 5-year vehicle-year bucket with both display totals and the
 * canonical-filtered ratio + sample-gate fields. Holds NO local KPI
 * math — the RPC is the source of truth.
 *
 * Replaces the local `loadYearGroupStats` aggregation in
 * `YearGroupPerformance.tsx`. The component continues to handle
 * sorting / formatting / display arrangement client-side. Per-tech
 * drill-down stays inline (separate aggregation shape — out of K-T-11
 * scope).
 *
 * Year buckets mirror the JS `analyticsHelpers.getYearGroup(year)`
 * helper exactly (EN DASH separator U+2013, e.g. `'2016–2020'`); the
 * SQL CASE expression in the migration encodes the same arithmetic
 * (floor((year - 1) / 5) * 5 + 1 .. + 5).
 *
 * Date-range conversion: shop-local `yyyy-MM-dd` strings → UTC ISO
 * timestamps via `fromZonedTime`, mirroring K-T-10 and KPI-R71-FIX.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { fromZonedTime } from 'date-fns-tz';
import type { VehicleYearGroupEfficiencyResult } from '../../../supabase/functions/_shared/kpiCanonical';

export interface UseVehicleYearGroupEfficiencyOptions {
  shopId: string;
  /** Shop-local `yyyy-MM-dd`. */
  startDate: string;
  /** Shop-local `yyyy-MM-dd`. */
  endDate: string;
  shopTimezone: string;
}

export interface UseVehicleYearGroupEfficiencyResult {
  data: VehicleYearGroupEfficiencyResult[] | null;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}

/**
 * Returns the canonical per-year-bucket efficiency tuple for the given
 * shop + date range. The hook converts the shop-local date strings to
 * UTC ISO timestamps before invoking the RPC.
 *
 * Stable query key:
 *   ['kpi', 'kpi_efficiency_by_vehicle_year_group', shopId, startDate,
 *    endDate, shopTimezone]
 */
export function useVehicleYearGroupEfficiency(
  opts: UseVehicleYearGroupEfficiencyOptions,
): UseVehicleYearGroupEfficiencyResult {
  const query = useQuery({
    queryKey: [
      'kpi',
      'kpi_efficiency_by_vehicle_year_group',
      opts.shopId,
      opts.startDate,
      opts.endDate,
      opts.shopTimezone,
    ],
    queryFn: async (): Promise<VehicleYearGroupEfficiencyResult[] | null> => {
      const startAt = fromZonedTime(
        `${opts.startDate}T00:00:00`,
        opts.shopTimezone,
      ).toISOString();
      const endAt = fromZonedTime(
        `${opts.endDate}T23:59:59.999`,
        opts.shopTimezone,
      ).toISOString();

      // Cast through unknown until supabase types.ts codegen catches up.
      const { data, error } = await (supabase.rpc as unknown as (
        fn: string,
        args: { p_shop_id: string; p_start_at: string; p_end_at: string },
      ) => Promise<{
        data: VehicleYearGroupEfficiencyResult[] | null;
        error: unknown;
      }>)('kpi_efficiency_by_vehicle_year_group', {
        p_shop_id: opts.shopId,
        p_start_at: startAt,
        p_end_at: endAt,
      });

      if (error) throw error;
      return data ?? [];
    },
    enabled: Boolean(
      opts.shopId &&
        opts.startDate &&
        opts.endDate &&
        opts.shopTimezone,
    ),
    staleTime: 5 * 60 * 1000,
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}
